import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import classNames from 'classnames';
import { Button } from '~/components/buttons/Button/Button';
import { FacilityFormValues } from '~/features/types';

/*
 * [MCV-1836] Stacking tier. The default suits the vault Employment and Training forms. A caller
 * that shows this dialog on top of an "Add New Facility" FormDialog must pass the tier of that
 * dialog: each value here sits just above the FormDialog tier of the same name (default z-10,
 * nested z-[70], top z-[80]), so the confirm dialog is not left behind the form it was raised from.
 */
const Z_INDEX_CLASSES = {
  default: 'z-20',
  nested: 'z-[75]',
  top: 'z-[85]',
} as const;

interface DuplicateFacilityDialogProps {
  open: boolean | undefined;
  duplicates: FacilityFormValues[] | undefined;
  pendingName: string | undefined;
  onSelectExisting: (facility: FacilityFormValues) => void;
  onConfirmCreate: () => void;
  onBack: () => void;
  zIndex?: keyof typeof Z_INDEX_CLASSES;
}

export const DuplicateFacilityDialog = ({
  open,
  duplicates,
  pendingName,
  onSelectExisting,
  onConfirmCreate,
  onBack,
  zIndex = 'default',
}: DuplicateFacilityDialogProps) => {
  return (
    <Dialog
      open={!!open}
      onClose={onBack}
      className={classNames('relative', Z_INDEX_CLASSES[zIndex])}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-grey-100/60 backdrop-blur-sm transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />
      <div
        className={classNames('fixed inset-0 w-screen overflow-y-auto', Z_INDEX_CLASSES[zIndex])}
      >
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel
            transition
            className="relative transform rounded-xl bg-white px-6 pb-6 pt-5 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-lg"
          >
            <DialogTitle as="h3" className="text-lg font-semibold leading-6 text-grey-100">
              Similar Facilities Found
            </DialogTitle>
            <p className="mt-2 text-sm text-grey-600">
              We found existing facilities similar to &quot;<strong>{pendingName ?? ''}</strong>
              &quot;. Would you like to use one of these instead?
            </p>

            <ul className="mt-4 divide-y divide-grey-900 border border-grey-900 rounded-lg overflow-hidden">
              {(duplicates ?? []).map((facility) => (
                <li key={facility.value || facility.label}>
                  <button
                    type="button"
                    className="w-full px-4 py-3 text-left hover:bg-primary-t-900 transition-colors cursor-pointer"
                    onClick={() => onSelectExisting(facility)}
                  >
                    <p className="font-medium text-grey-900">{facility.label}</p>
                    {facility.address && (
                      <p className="text-sm text-grey-500 mt-0.5">
                        {[
                          facility.address.addressLine1,
                          facility.address.city,
                          typeof facility.address.stateCode === 'object'
                            ? facility.address.stateCode.label
                            : facility.address.stateCode,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    )}
                  </button>
                </li>
              ))}
            </ul>

            <div className="mt-6 flex justify-end gap-3">
              <Button type="button" colorVariant="secondary" onClick={onBack}>
                Back
              </Button>
              <Button type="button" colorVariant="primary" onClick={onConfirmCreate}>
                Add New Anyway
              </Button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
